import { NextPageContext } from 'next';
import Head from 'next/head';

import { redirectTo404 } from 'lib/constants';
import { getCurrentUserFromContext } from 'server/api/services/auth';
import { isRequestFromGondolaApp, serialize } from 'lib/helpers';
import { ProfileUser } from 'lib/types/users';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { ReactElement, useState } from 'react';
import { Replay as ReplayService } from 'server/api/services/replay';
import { UserService } from 'server/api/services/users/user';

import { Replay2025 } from 'features/Replay2025/Replay2025';
import { ReplayCountdown } from 'features/Replay2025/Countdown';
import { Post } from 'lib/types';
import { CardUrls, Horoscope, UserSnapshot } from 'lib/types/replay';
import { notify } from 'lib/utils/honeybadgerUtils';
import { imagePostUrl, webAssetUrl } from 'lib/utils/imageUtils';

const FTM_AND_GONDOLA = [9, 227];

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;

  try {
    const currentUser = serialize(await getCurrentUserFromContext(context));

    if (!currentUser) {
      return {
        props: {
          profileUser: null,
          isFromGondolaApp: isRequestFromGondolaApp(context?.req),
          overallStats: null,
          topPosts: null,
          procrastinationScore: null,
          topRoles: null,
          topLocationRole: null,
          allTimeStats: null,
          topRolesWithCount: null,
          horoscope: null,
          startOfYearStats: null,
          cardUrls: null,
        },
      };
    }
    const { profileuser: profileInUrl } = query;
    const username = profileInUrl || currentUser.username;

    const profileUser = await UserService.getProfileByUsername(username, currentUser.id);

    if (!profileUser) {
      return redirectTo404;
    }

    const profileUserId = profileUser.id;
    if (profileUserId !== currentUser.id) {
      // Generally we don't allow users to view other users' Replay.
      // BUT: An exception is for FTM and Gondola, or if the user is an admin
      if (!FTM_AND_GONDOLA.includes(profileUserId) && !currentUser.isAdmin) {
        return redirectTo404;
      }
    }

    const replay = new ReplayService(profileUserId);
    const stats = await replay.fetchReplayStats();

    const {
      yearlyStats,
      allTimeStats,
      topPosts,
      topLocationRole,
      procrastinationScore,
      topRolesWithCount,
      horoscope,
      startOfYearStats,
      cardUrls,
    } = stats;

    return {
      props: {
        profileUser: serialize(profileUser),
        isFromGondolaApp: isRequestFromGondolaApp(context?.req),
        overallStats: serialize(yearlyStats),
        topPosts: serialize(topPosts),
        procrastinationScore: serialize(procrastinationScore || null),
        topRoles: serialize(topRolesWithCount),
        topLocationRole: serialize(topLocationRole),
        allTimeStats: serialize(allTimeStats),
        horoscope: serialize(horoscope),
        startOfYearStats: serialize(startOfYearStats),
        cardUrls: serialize(cardUrls),
      },
    };
  } catch (err: any) {
    notify(err, 'Replay2025', {
      query,
    });
    return redirectTo404;
  }
};

interface Props {
  profileUser: ProfileUser;
  isFromGondolaApp: boolean;
  overallStats: any;
  topPosts: Post[];
  procrastinationScore: number | null;
  topRoles: any;
  topLocationRole: any;
  allTimeStats: any;
  horoscope: Horoscope;
  startOfYearStats: UserSnapshot;
  cardUrls: CardUrls;
}
const ReplayPage = ({
  profileUser, isFromGondolaApp, overallStats,
  topPosts, procrastinationScore, topRoles, topLocationRole,
  allTimeStats, horoscope, startOfYearStats, cardUrls,
}: Props) => {
  const background = isFromGondolaApp ? 'bg-black' : 'bg-darkgray';
  const [canAccessReplay, setCanAccessReplay] = useState(false);

  if (!profileUser) {
    return (
      <div className={background}>
        <GondolaHead
          title="Gondola Replay 2025"
          image={webAssetUrl('meta-data-cards/2025-Replay-Pro.png')}
          description="Your personalized, shareable snapshot of the incredible impact you've had in the world of content throughout the past year!"
          url="https://gondola.cc/replay"
          cardUrl={webAssetUrl('meta-data-cards/2025-Replay-Pro.png')}
        />
        <ReplayCountdown setCanAccessReplay={setCanAccessReplay} profileUser={profileUser} />
      </div>
    );
  }
  const renderImageLinks = () => {
    const posts = topPosts.map((post) => (
      <link
        key={post.thumbnailUrl}
        rel="preload"
        href={imagePostUrl(post.thumbnailUrl, {
          width: 300,
          height: 300,
        })}
        as="image"
      />
    ));

    const posts800 = topPosts.map((post) => (
      <link
        rel="preload"
        key={post.thumbnailUrl}
        href={imagePostUrl(post.thumbnailUrl, {
          width: 800,
          height: 800,
        })}
        as="image"
      />
    ));
    const horoscopeImg = (
      <link
        rel="preload"
        key={horoscope?.imageUrl}
        href={horoscope?.imageUrl}
        as="image"
      />
    );

    return [...posts, ...posts800, horoscopeImg];
  };

  return (
    <div className={background}>
      <GondolaHead
        title={`${profileUser.name}'s Replay 2025`}
        image={profileUser.avatarUrl}
        description="A personalized, shareable snapshot of the incredible impact you've had in the world of content throughout the past year!"
        url="https://gondola.cc/replay"
        cardUrl={webAssetUrl('meta-data-cards/2025-Replay.png')}
      />
      <Head>
        {renderImageLinks()}
      </Head>

      {!canAccessReplay
        ? <ReplayCountdown setCanAccessReplay={setCanAccessReplay} profileUser={profileUser} />
        : (
          <Replay2025
            profileUser={profileUser}
            isFromGondolaApp={isFromGondolaApp}
            overallStats={overallStats}
            topPosts={topPosts}
            procrastinationScore={procrastinationScore}
            topRoles={topRoles}
            topLocationRole={topLocationRole}
            allTimeStats={allTimeStats}
            horoscope={horoscope}
            startOfYearStats={startOfYearStats}
            cardUrls={cardUrls}
          />
        )}
    </div>
  );
};
ReplayPage.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};

// eslint-disable-next-line import/no-default-export
export default ReplayPage;
